import { ChartContainer } from "@patient-watch/ui/chart";
import NumberFlow from "@number-flow/react";
import { useId, useState } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ReferenceArea,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { DueEntryT, EntryViewT } from "@patient-watch/types/database";
import {
    DEFAULT_LOCALE,
    formatLocaleChartDate,
    localizeStoredScheduleLabel,
    type SupportedLocale,
} from "@patient-watch/i18n";
import { CHART_CONSTANTS, createTooltipProps, createXAxisProps, createYAxisProps } from "../chartConstants";
import {
    buildYAxisTicks,
    convertEntriesToChartConfig,
    convertEntriesToChartData,
    expandYDomainForCurve,
    CHART_SCORE_NUMBER_FORMAT,
    formatChartScoreDisplay,
    getChartDataNumericExtents,
    getChartSeriesMetadata,
    getFixedChartYDomain,
    getMinMaxBoundsFromEntryValues,
    getNonChartedPrimaryMeta,
    hasChartOrderMetadata,
    hasRatingsListValues,
    roundChartScore,
    shouldShowScoreFraction,
} from "./utils";
import {
    type ChartBackgroundBand,
    buildBmiYAxisTicks,
    clipBackgroundBandsToDomain,
    isBmiForm,
    resolveBmiYDomain,
} from "../bmiChart";

export type ChartYAxisMode = "full" | "fit" | "custom";
export type ChartYAxisScale = "linear" | "log";

export interface ChartYAxisOptions {
    /** full = clinical/form scale (default); fit = tight around plotted values; custom = min/max. */
    mode?: ChartYAxisMode;
    min?: number;
    max?: number;
    scale?: ChartYAxisScale;
}

interface GenericChartProps {
    entries: EntryViewT[];
    formId: string;
    /** Tracker schedule slots; used to plot baseline/day steps at the correct due dates. */
    processedSchedule?: DueEntryT[] | null;
    printMode?: boolean;
    /** Smaller chart and data panel for narrow phone mockups. */
    compact?: boolean;
    /** Override default chart height (number in px, or CSS length e.g. "30vh"). */
    chartHeight?: number | string;
    /** Drives chart variant. 'stacked_area' stacks subscores additively; 'bar' shows discrete bars; 'stacked_bar' stacks the bars of one entry; 'line' is the default. */
    chartType?: "line" | "stacked_area" | "bar" | "stacked_bar";
    locale?: SupportedLocale;
    /**
     * Palette slot 1-5 for this chart instance. Series colours start at this slot and wrap
     * through --chart-1…--chart-5. Used by dashboard grids to differentiate adjacent widgets.
     */
    colorIndex?: number;
    /** Optional Y-axis domain / scale overrides (analytics builder). */
    yAxis?: ChartYAxisOptions;
    /** Horizontal category bands painted behind the series (e.g. BMI ranges). */
    backgroundBands?: ChartBackgroundBand[];
    /** When false, line charts skip the gradient fill so background bands stay visible. */
    fillLineArea?: boolean;
    /**
     * Mini tracker chart: same series, domain, and chart type, without axes, grid, tooltip, or the data panel.
     */
    spark?: boolean;
}

/** Compact / facet charts: taller on wider viewports. */
const COMPACT_CHART_HEIGHT_CLASS =
    "h-[200px] min-h-[200px] sm:h-[240px] sm:min-h-[240px] md:h-[280px] md:min-h-[280px] lg:h-[320px] lg:min-h-[320px]";

/** Default tracker charts: taller on wider viewports. */
const DEFAULT_CHART_HEIGHT_CLASS =
    "h-[280px] min-h-[280px] sm:h-[320px] sm:min-h-[320px] md:h-[360px] md:min-h-[360px] lg:h-[400px] lg:min-h-[400px]";

function resolveSeriesColor(seriesIndex: number, colorIndex?: number, metaColor?: string): string {
    if (colorIndex != null && Number.isFinite(colorIndex)) {
        const slot = ((((Math.round(colorIndex) - 1 + seriesIndex) % 5) + 5) % 5) + 1;
        return `hsl(var(--chart-${slot}))`;
    }
    return metaColor ?? `hsl(var(--chart-${(seriesIndex % 5) + 1}))`;
}

/** Primary totals must not be stacked or listed again under a computed TOTAL badge. */
function isPrimaryTotalLabel(label: string): boolean {
    const normalized = label.trim().toLowerCase();
    return normalized === "total" || normalized.endsWith(" total score") || normalized.endsWith(" total");
}

function ScoreBadge({
    label,
    value,
    maxScore,
    highlighted = false,
    compact = false,
    formId,
}: {
    label: string;
    value: number | string | undefined;
    maxScore?: number | null;
    highlighted?: boolean;
    compact?: boolean;
    formId?: string;
}) {
    const isNumeric = typeof value === "number" && !Number.isNaN(value);
    const showFraction = formId ? shouldShowScoreFraction(formId, maxScore) : maxScore != null;
    const displayValue = isNumeric ? roundChartScore(value as number) : value;
    return (
        <div className={`flex justify-between items-center ${highlighted || compact ? "" : "text-xs"}`}>
            <span
                className={
                    highlighted
                        ? compact
                            ? "font-semibold text-slate-700 text-xs uppercase tracking-wide"
                            : "font-bold text-slate-700 text-base uppercase tracking-wider"
                        : "text-xs text-slate-500"
                }
            >
                {label}
            </span>
            <span
                className={
                    highlighted
                        ? compact
                            ? "font-semibold text-indigo-600 text-xs"
                            : "text-indigo-600 font-semibold text-base"
                        : "font-medium text-slate-700 text-xs"
                }
            >
                {isNumeric ? (
                    formId && !showFraction ? (
                        formatChartScoreDisplay(value as number, formId, maxScore)
                    ) : (
                        <>
                            <NumberFlow value={displayValue as number} format={CHART_SCORE_NUMBER_FORMAT} />
                            {showFraction && maxScore != null && <span>/{maxScore}</span>}
                        </>
                    )
                ) : (
                    (value ?? "—")
                )}
            </span>
        </div>
    );
}

function SeriesColorDot({ color, compact = false }: { color: string; compact?: boolean }) {
    return (
        <span
            className={`border-2 rounded-full aspect-square shrink-0 ${compact ? "w-2.5 h-2.5" : "w-4 h-4"}`}
            style={{ backgroundColor: `${color} / 0.5`, borderColor: color }}
        />
    );
}

/**
 * Returns a function that strips the shared word-boundary prefix from a label.
 * e.g. ["FAOS Pain Score", "FAOS ADL Score"] → strips "FAOS " → "Pain Score", "ADL Score"
 * Not stripped when the remainder would start mid-sentence in lower case.
 */
export function buildDisplayLabel(labels: string[]): (label: string) => string {
    if (labels.length <= 1) return (l) => l;
    const parts = labels[0].split(" ");
    let commonCount = 0;
    for (let i = 1; i <= parts.length; i++) {
        const candidate = `${parts.slice(0, i).join(" ")} `;
        if (labels.every((l) => l.startsWith(candidate))) {
            commonCount = i;
        } else {
            break;
        }
    }
    if (commonCount === 0) return (l) => l;
    const prefix = `${parts.slice(0, commonCount).join(" ")} `;
    // A question's shared opening ("Please rate on a scale 0-10 how") leaves a fragment that starts mid-sentence
    // ("itchy the skin..."). Keep the full label then rather than show a lowercase fragment.
    const remainderStartsMidSentence = (l: string) => {
        const rest = l.slice(prefix.length);
        return rest.length > 0 && rest[0] === rest[0]?.toLowerCase() && rest[0] !== rest[0]?.toUpperCase();
    };
    return (label: string) => {
        if (!label.startsWith(prefix) || remainderStartsMidSentence(label)) return label;
        return label.slice(prefix.length);
    };
}

function niceFitDomain(dataMin: number, dataMax: number): [number, number] {
    if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return [0, 10];
    if (dataMin === dataMax) {
        const pad = Math.max(Math.abs(dataMin) * 0.1, 1);
        return [dataMin - pad, dataMax + pad];
    }
    const span = dataMax - dataMin;
    const rough = span / 4;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const residual = rough / magnitude;
    const step = residual >= 5 ? 5 * magnitude : residual >= 2 ? 2 * magnitude : magnitude;
    const lo = Math.floor(dataMin / step) * step;
    const hi = Math.ceil(dataMax / step) * step;
    return [lo === hi ? lo - step : lo, lo === hi ? hi + step : hi];
}

export function GenericChart({
    entries,
    formId,
    processedSchedule,
    printMode = false,
    compact = false,
    chartHeight,
    chartType = "line",
    locale = DEFAULT_LOCALE,
    colorIndex,
    yAxis,
    backgroundBands,
    fillLineArea = true,
    spark = false,
}: GenericChartProps) {
    const gradientIdPrefix = useId().replace(/:/g, "");
    // Ratings-list activity JSON (PSFS / MS-ADL) must win over chart_order on aggregate scores,
    // otherwise we plot "PSFS Average Score" while chart data only has activity keys → empty chart.
    const usesRatingsList = hasRatingsListValues(entries);
    const hasMeta = !usesRatingsList && hasChartOrderMetadata(entries);
    const seriesMeta = hasMeta ? getChartSeriesMetadata(entries, formId) : [];
    const primaryMeta = hasMeta ? getNonChartedPrimaryMeta(entries, formId) : null;

    const chartConfig = convertEntriesToChartConfig(entries, formId);

    // Build a display-label function that strips a shared word-boundary prefix,
    // e.g. ["FAOS Pain Score", "FAOS ADL Score"] → displayLabel strips "FAOS "
    const displayLabel = buildDisplayLabel([
        ...seriesMeta.map((s) => s.label),
        ...(primaryMeta ? [primaryMeta.label] : []),
        ...Object.keys(chartConfig),
    ]);

    const renderNumericScore = (raw: number, maxScore?: number | null) => {
        if (!shouldShowScoreFraction(formId, maxScore)) {
            return formatChartScoreDisplay(raw, formId, maxScore);
        }
        return (
            <>
                <NumberFlow value={roundChartScore(raw)} format={CHART_SCORE_NUMBER_FORMAT} />/{maxScore}
            </>
        );
    };

    let chartData = convertEntriesToChartData(entries, formId, processedSchedule).map((point) => ({
        ...point,
        timing: point.timing ? localizeStoredScheduleLabel(String(point.timing), locale) : point.timing,
        timing_label: point.timing_label
            ? localizeStoredScheduleLabel(String(point.timing_label), locale)
            : point.timing_label,
    }));

    const timingLabel =
        locale === "de-DE"
            ? "Zeitpunkt"
            : locale === "da-DK"
              ? "Tidspunkt"
              : locale === "fr-FR"
                ? "Échéance"
                : locale === "it-IT"
                  ? "Tempistica"
                  : locale === "es-ES"
                    ? "Momento"
                    : "Timing";
    const dateLabel =
        locale === "de-DE"
            ? "Datum"
            : locale === "da-DK"
              ? "Dato"
              : locale === "it-IT"
                ? "Data"
                : locale === "es-ES"
                  ? "Fecha"
                  : "Date";

    const isAnimated = !printMode;

    const seriesKeys = seriesMeta.map((s) => s.label);

    const [activeDataPoint, setActiveDataPoint] = useState<any>(chartData[0] || null);

    // ─── Y-axis domain ─────────────────────────────────────────────────────────
    let yMin = 0;
    let yMax: number;

    /** When chart_order is set we normally use max_score per series; if that ceiling is missing on payload, this stays <= 0 and we fall back to min/max from input definitions. */
    let metaAxisMax = 0;

    if (hasMeta) {
        if (chartType === "stacked_area") {
            metaAxisMax = seriesMeta.reduce((sum, s) => sum + (s.maxScore ?? 0), 0);
            yMax = metaAxisMax > 0 ? metaAxisMax : 100;
        } else if (chartType === "bar" || chartType === "stacked_bar") {
            const positives = seriesMeta.map((s) => s.maxScore ?? 0).filter((v) => v > 0);
            metaAxisMax = positives.length > 0 ? Math.max(...positives) : 0;
            yMax = metaAxisMax > 0 ? metaAxisMax : 100;
        } else {
            const positives = seriesMeta.map((s) => s.maxScore ?? 0).filter((v) => v > 0);
            metaAxisMax = positives.length > 0 ? Math.max(...positives) : 0;
            yMax = metaAxisMax > 0 ? metaAxisMax : 100;
        }

        let { min, max } = getMinMaxBoundsFromEntryValues(entries, formId, { chartOrderOnly: true });
        if (max == null) {
            ({ min, max } = getMinMaxBoundsFromEntryValues(entries, formId, { chartOrderOnly: false }));
        }

        if (metaAxisMax <= 0) {
            yMin = min ?? 0;
            yMax = max ?? 100;
        } else if (
            chartType !== "stacked_area" &&
            chartType !== "stacked_bar" &&
            max != null &&
            max > 0 &&
            max < yMax
        ) {
            yMin = min ?? 0;
            yMax = max;
        }
    } else {
        const { min, max } = getMinMaxBoundsFromEntryValues(entries, formId, { chartOrderOnly: false });
        yMin = min ?? 0;
        yMax = max ?? 100;
        // PSFS / MS-ADL activity stacks: each series is 0-10, so stack ceiling is n × 10.
        if (chartType === "stacked_area" && hasRatingsListValues(entries) && max != null && max > 0) {
            const seriesCount = Object.keys(chartConfig).length;
            if (seriesCount > 0) {
                yMax = seriesCount * max;
            }
        }
    }

    const lineDataKeysForDomain = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
    const usesCurvedLines = chartType === "stacked_area" || chartType === "line";
    const { min: dataMin, max: dataMax } = getChartDataNumericExtents(chartData, lineDataKeysForDomain, chartType);
    const yAxisMode = yAxis?.mode ?? "full";
    const yAxisScale = yAxis?.scale === "log" ? "log" : "linear";

    if (yAxisMode === "custom") {
        yMin = yAxis?.min ?? dataMin ?? yMin;
        yMax = yAxis?.max ?? dataMax ?? yMax;
        if (yMin > yMax) [yMin, yMax] = [yMax, yMin];
        if (yMin === yMax) {
            yMin -= 1;
            yMax += 1;
        }
    } else if (yAxisMode === "fit" && dataMin != null && dataMax != null) {
        [yMin, yMax] = niceFitDomain(dataMin, dataMax);
    } else if (isBmiForm(formId)) {
        [yMin, yMax] = resolveBmiYDomain(dataMin, dataMax);
    } else {
        const fixedYDomain = getFixedChartYDomain(formId);
        if (fixedYDomain) {
            [yMin, yMax] = fixedYDomain;
        } else {
            [yMin, yMax] = expandYDomainForCurve(yMin, yMax, dataMin, dataMax, {
                usesCurvedLines,
                dotRadius: 5,
            });
        }
    }

    if (yAxisScale === "log") {
        if (yMax <= 0) yMax = 1;
        if (yMin <= 0) yMin = Math.min(yMax / 10, 0.1);
        if (yMin >= yMax) yMin = yMax / 10;
    }

    const ticks =
        yAxisScale === "log"
            ? undefined
            : isBmiForm(formId)
              ? buildBmiYAxisTicks(yMin, yMax)
              : buildYAxisTicks(yMin, yMax);
    const visibleBackgroundBands = clipBackgroundBandsToDomain(backgroundBands ?? [], yMin, yMax);

    // ─── Chart content ─────────────────────────────────────────────────────────
    const axisOverrides = compact
        ? { axisFontSize: 10, yAxisWidth: isBmiForm(formId) ? 36 : 26, axisTickMargin: 4, yAxisTickMargin: 2 }
        : isBmiForm(formId)
          ? { yAxisWidth: 42 }
          : undefined;

    const animateSeries = isAnimated && !spark;
    const seriesStrokeWidth = spark ? 1.75 : 2;
    const sparkDot = chartData.length === 1 ? { r: 2 } : false;
    const axisAndGrid = spark ? (
        <>
            <XAxis dataKey="timing" hide height={0} />
            <YAxis
                hide
                width={0}
                domain={[yMin, yMax]}
                scale={yAxisScale}
                allowDataOverflow={yAxisScale === "log" || yAxisMode === "custom"}
            />
        </>
    ) : (
        <>
            {visibleBackgroundBands.map((band) => (
                <ReferenceArea
                    key={band.label}
                    y1={band.y1}
                    y2={band.y2}
                    fill={band.fill}
                    fillOpacity={1}
                    strokeOpacity={0}
                    ifOverflow="hidden"
                />
            ))}
            <CartesianGrid vertical={false} />
            <XAxis {...createXAxisProps(printMode, axisOverrides)} />
            <YAxis
                {...createYAxisProps([yMin, yMax], ticks, printMode, axisOverrides)}
                scale={yAxisScale}
                allowDataOverflow={yAxisScale === "log" || yAxisMode === "custom"}
            />
            {!printMode && <Tooltip {...createTooltipProps()} />}
        </>
    );

    let chartContent: React.ReactNode;

    if (chartType === "stacked_area") {
        const rawKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
        const dataKeys = rawKeys.filter((key) => !isPrimaryTotalLabel(key));
        const areas = dataKeys.map((key, index) => {
            const meta = seriesMeta.find((s) => s.label === key);
            const color = resolveSeriesColor(index, colorIndex, meta?.color);
            return (
                <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.5}
                    stackId="stack"
                    strokeWidth={seriesStrokeWidth}
                    isAnimationActive={animateSeries}
                    className={printMode ? `print:!stroke-[2px]` : undefined}
                />
            );
        });
        chartContent = (
            <>
                {axisAndGrid}
                {areas}
            </>
        );
    } else if (chartType === "bar" || chartType === "stacked_bar") {
        const dataKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
        const isStacked = chartType === "stacked_bar";
        const bars = dataKeys.map((key, index) => (
            <Bar
                key={key}
                dataKey={key}
                stackId={isStacked ? "stack" : undefined}
                fill={resolveSeriesColor(index, colorIndex, seriesMeta[index]?.color)}
                radius={isStacked ? undefined : [4, 4, 0, 0]}
                isAnimationActive={animateSeries}
            />
        ));
        chartContent = (
            <>
                {axisAndGrid}
                {bars}
            </>
        );
    } else {
        // Line chart - use AreaChart with gradient fill for ≤2 series, plain LineChart otherwise
        const dataKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
        const useGradient = fillLineArea && dataKeys.length <= 2 && visibleBackgroundBands.length === 0;

        if (useGradient) {
            const gradientDefs = dataKeys.map((key, index) => {
                const color = resolveSeriesColor(index, colorIndex, seriesMeta[index]?.color);
                return (
                    <linearGradient key={key} id={`${gradientIdPrefix}-line-${index}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                    </linearGradient>
                );
            });

            const areas = dataKeys.map((key, index) => {
                const color = resolveSeriesColor(index, colorIndex, seriesMeta[index]?.color);
                return (
                    <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        fill={`url(#${gradientIdPrefix}-line-${index})`}
                        strokeWidth={seriesStrokeWidth}
                        dot={spark ? (sparkDot ? { ...sparkDot, fill: color } : false) : { r: 3, fill: color }}
                        activeDot={spark ? false : { r: 5 }}
                        isAnimationActive={animateSeries}
                        className={printMode ? `print:!stroke-[2px]` : undefined}
                    />
                );
            });

            chartContent = (
                <>
                    <defs>{gradientDefs}</defs>
                    {axisAndGrid}
                    {areas}
                </>
            );
        } else {
            const lines = dataKeys.map((key, index) => (
                <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={resolveSeriesColor(index, colorIndex, seriesMeta[index]?.color)}
                    strokeWidth={seriesStrokeWidth}
                    dot={spark ? sparkDot : { r: 3 }}
                    activeDot={spark ? false : { r: 5 }}
                    isAnimationActive={animateSeries}
                    className={printMode ? `print:!stroke-[2px]` : undefined}
                />
            ));

            chartContent = (
                <>
                    {axisAndGrid}
                    {lines}
                </>
            );
        }
    }

    // ─── Data panel ────────────────────────────────────────────────────────────
    const panelGap = compact ? "gap-1" : "gap-2";
    const panelMargin = compact ? "mt-2" : "mt-4";
    const headerLabelClass = "text-slate-500 text-xs uppercase tracking-wide";
    const headerValueClass = "font-semibold text-slate-900 text-xs";
    const headerDateClass = "font-medium text-slate-700 text-xs";
    const rowClass = "flex justify-between items-center text-xs";

    const renderDataPanel = () => {
        if (!activeDataPoint) return null;

        const header = (
            <>
                <div className="flex justify-between items-center">
                    <span className={headerLabelClass}>{timingLabel}</span>
                    <span className={headerValueClass}>{activeDataPoint.timing}</span>
                </div>
                <div className="flex justify-between items-center">
                    <span className={headerLabelClass}>{dateLabel}</span>
                    <span className={headerDateClass}>{formatLocaleChartDate(activeDataPoint.date, locale)}</span>
                </div>
                {typeof activeDataPoint.patients === "number" && (
                    <div className="flex justify-between items-center">
                        <span className={headerLabelClass}>Patients</span>
                        <span className={headerValueClass}>n = {activeDataPoint.patients}</span>
                    </div>
                )}
                <hr />
            </>
        );

        if ((chartType === "bar" || chartType === "stacked_bar") && hasMeta && seriesMeta.length > 0) {
            return (
                <div className={`flex flex-col ${panelGap} ${panelMargin}`}>
                    {header}
                    {seriesMeta.map((s) => {
                        const raw = activeDataPoint[s.label];
                        const isYesNo = s.maxScore === 1;
                        const display = isYesNo
                            ? raw === 1
                                ? "Yes"
                                : raw === 0
                                  ? "No"
                                  : "—"
                            : typeof raw === "number" && !Number.isNaN(raw)
                              ? undefined
                              : "—";
                        return (
                            <div key={s.label} className={rowClass}>
                                <span className="flex items-center gap-2 text-slate-500 text-xs">
                                    <SeriesColorDot color={s.color} compact={compact} />
                                    {displayLabel(s.label)}
                                </span>
                                <span className="font-medium text-slate-700">
                                    {display !== undefined
                                        ? display
                                        : typeof raw === "number" && !Number.isNaN(raw)
                                          ? renderNumericScore(raw, s.maxScore)
                                          : "—"}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (chartType === "stacked_area") {
            const rawKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
            const dataKeys = rawKeys.filter((key) => !isPrimaryTotalLabel(key));
            const primaryTotalKey =
                primaryMeta?.label ??
                Object.keys(activeDataPoint).find(
                    (key) => isPrimaryTotalLabel(key) && typeof activeDataPoint[key] === "number"
                );
            const stackedSum = dataKeys.reduce((sum, k) => {
                const v = activeDataPoint[k];
                return sum + (typeof v === "number" && !Number.isNaN(v) ? v : 0);
            }, 0);
            const primaryTotalValue =
                primaryTotalKey != null && typeof activeDataPoint[primaryTotalKey] === "number"
                    ? (activeDataPoint[primaryTotalKey] as number)
                    : stackedSum;
            const total = roundChartScore(primaryTotalValue);
            const totalMax =
                primaryMeta?.maxScore ?? (hasMeta ? seriesMeta.reduce((sum, s) => sum + (s.maxScore ?? 0), 0) : null);
            return (
                <div className={`flex flex-col ${panelGap} ${panelMargin}`}>
                    {header}
                    <ScoreBadge
                        label="Total"
                        value={total}
                        maxScore={totalMax || null}
                        highlighted
                        compact={compact}
                        formId={formId}
                    />
                    {dataKeys.map((key, index) => {
                        const meta = seriesMeta.find((s) => s.label === key);
                        const color = resolveSeriesColor(index, colorIndex, meta?.color);
                        return (
                            <div key={key} className={rowClass}>
                                <span className="flex items-center gap-2 text-slate-500 text-xs">
                                    <SeriesColorDot color={color} compact={compact} />
                                    {displayLabel(key)}
                                </span>
                                <span className="font-medium text-slate-700 text-xs">
                                    {typeof activeDataPoint[key] === "number" && !Number.isNaN(activeDataPoint[key])
                                        ? renderNumericScore(activeDataPoint[key], meta?.maxScore)
                                        : "—"}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (primaryMeta) {
            const dataKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
            return (
                <div className={`flex flex-col ${panelGap} ${panelMargin}`}>
                    {header}
                    <ScoreBadge
                        label={displayLabel(primaryMeta.label)}
                        value={activeDataPoint[primaryMeta.label]}
                        maxScore={primaryMeta.maxScore}
                        highlighted
                        formId={formId}
                    />
                    {seriesMeta.map((s, index) => (
                        <div key={s.label} className="flex justify-between items-center text-xs">
                            <span className="flex items-center gap-2 text-slate-500">
                                <SeriesColorDot color={resolveSeriesColor(index, colorIndex, s.color)} />
                                {displayLabel(s.label)}
                            </span>
                            <span className="font-medium text-slate-700">
                                {typeof activeDataPoint[s.label] === "number" && !Number.isNaN(activeDataPoint[s.label])
                                    ? renderNumericScore(activeDataPoint[s.label], s.maxScore)
                                    : "—"}
                            </span>
                        </div>
                    ))}
                    {/* Show any remaining non-charted keys if no seriesMeta */}
                    {!hasMeta &&
                        dataKeys
                            .filter((k) => k !== primaryMeta.label)
                            .map((key, index) => (
                                <div key={key} className="flex justify-between items-center text-xs">
                                    <span className="flex items-center gap-2 text-slate-500">
                                        <SeriesColorDot color={resolveSeriesColor(index, colorIndex)} />
                                        {displayLabel(key)}
                                    </span>
                                    <span className="font-medium text-slate-700">
                                        {typeof activeDataPoint[key] === "number" &&
                                        !Number.isNaN(activeDataPoint[key]) ? (
                                            <NumberFlow
                                                value={roundChartScore(activeDataPoint[key] as number)}
                                                format={CHART_SCORE_NUMBER_FORMAT}
                                            />
                                        ) : (
                                            (activeDataPoint[key] ?? "—")
                                        )}
                                    </span>
                                </div>
                            ))}
                </div>
            );
        }

        // Default: show all chart series with equal styling.
        // All-caps/indigo highlighting is reserved for the primaryMeta (total) branch above.
        const dataKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
        return (
            <div className={`flex flex-col ${panelGap} ${panelMargin}`}>
                {header}
                {dataKeys.map((key, index) => {
                    const meta = seriesMeta.find((s) => s.label === key);
                    const color = resolveSeriesColor(index, colorIndex, meta?.color);
                    return (
                        <div key={key} className="flex justify-between items-center text-xs">
                            <span className="flex items-center gap-2 text-slate-500">
                                <SeriesColorDot color={color} />
                                {displayLabel(key)}
                            </span>
                            <span className="font-medium text-slate-700">
                                {typeof activeDataPoint[key] === "number" && !Number.isNaN(activeDataPoint[key])
                                    ? renderNumericScore(activeDataPoint[key], meta?.maxScore)
                                    : (activeDataPoint[key] ?? "—")}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ─── Render ────────────────────────────────────────────────────────────────
    const lineDataKeys = hasMeta && seriesMeta.length > 0 ? seriesKeys : Object.keys(chartConfig);
    const useGradientLine =
        chartType === "line" && fillLineArea && lineDataKeys.length <= 2 && visibleBackgroundBands.length === 0;
    const ChartWrapper =
        chartType === "stacked_area" || useGradientLine
            ? AreaChart
            : chartType === "bar" || chartType === "stacked_bar"
              ? BarChart
              : LineChart;

    if (printMode) {
        const printMargin = {
            ...CHART_CONSTANTS.PRINT_MARGIN,
            top: usesCurvedLines ? 14 : CHART_CONSTANTS.PRINT_MARGIN.top,
        };
        return (
            <ChartContainer config={chartConfig} className="print-chart-fix">
                <ChartWrapper
                    width={CHART_CONSTANTS.PRINT_WIDTH}
                    height={CHART_CONSTANTS.PRINT_HEIGHT}
                    data={chartData}
                    margin={printMargin}
                >
                    {chartContent}
                </ChartWrapper>
            </ChartContainer>
        );
    }

    const chartMargin = spark
        ? { top: 2, right: 2, bottom: 2, left: 2 }
        : { ...CHART_CONSTANTS.MARGIN, top: usesCurvedLines ? 14 : CHART_CONSTANTS.MARGIN.top };
    const hasExplicitHeight = chartHeight != null;
    const chartHeightClass = spark
        ? "h-full min-h-0 w-full aspect-auto"
        : hasExplicitHeight
          ? "h-fit min-w-0 w-full aspect-auto"
          : `min-w-0 w-full aspect-auto ${compact ? COMPACT_CHART_HEIGHT_CLASS : DEFAULT_CHART_HEIGHT_CLASS}`;
    const chartContainerStyle = hasExplicitHeight
        ? { height: chartHeight, minHeight: typeof chartHeight === "number" ? chartHeight : undefined }
        : undefined;

    return (
        <div className={spark ? "w-full h-full min-h-0" : "flex flex-col min-w-0"}>
            <ChartContainer config={chartConfig} className={chartHeightClass} style={chartContainerStyle}>
                <ChartWrapper
                    data={chartData}
                    margin={chartMargin}
                    onMouseMove={
                        spark
                            ? undefined
                            : (state: any) => {
                                  if (state?.activePayload?.[0]) {
                                      setActiveDataPoint(state.activePayload[0].payload);
                                  }
                              }
                    }
                    onMouseLeave={
                        spark
                            ? undefined
                            : () => {
                                  if (chartData.length > 0) {
                                      setActiveDataPoint(chartData[0]);
                                  }
                              }
                    }
                >
                    {chartContent}
                </ChartWrapper>
            </ChartContainer>

            {spark ? null : renderDataPanel()}
        </div>
    );
}
